import { useBulkTableContext } from '@/components/bulkQueue/BulkTableContext';
import {
    VisitRequestTableRowFragment,
    VisitTableRowFragment,
} from '@/components/bulkQueue/tables/queries.gql.types';
import { useUnifyItemList } from '@/components/bulkQueue/tables/useBulkData';
import {
    Pagination,
    useUnifiedPaginationByPage,
} from '@/components/bulkQueue/tables/useUnifiedPagination';
import { ServerSideSort } from '@/components/tables/AxleTable';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { TypedDocumentNode, useQuery } from '@apollo/client';
import * as Types from 'gql-types';
import { useEffect } from 'react';

/**
 * Shared query result shape for all bulk assignment tables.
 * TVisitRequest/TVisit may include additional fragment fields
 * (e.g. services table adds ServicesAssignmentVisitRequest fields).
 */
type BulkQueryData<
    TVisitRequest extends VisitRequestTableRowFragment = VisitRequestTableRowFragment,
    TVisit extends VisitTableRowFragment = VisitTableRowFragment,
> = {
    visitRequests?: { totalCount: number; items: TVisitRequest[] } | null;
    visits?: { totalCount: number; items: TVisit[] } | null;
};

type UseConnectedBulkTableResult<
    TVisitRequest extends VisitRequestTableRowFragment,
    TVisit extends VisitTableRowFragment,
> = {
    rows: (TVisitRequest | TVisit)[];
    pagination: Pagination;
    serverSideSort: ServerSideSort;
    loading: boolean;
    data: BulkQueryData<TVisitRequest, TVisit> | undefined;
    showSkeleton: boolean;
};

export type QueryVariables = {
    visitFilters: Types.InputMaybe<Types.VisitFilter>;
    visitPagination: Types.InputMaybe<Types.PaginationInput>;
    visitOrdering: Types.InputMaybe<Types.VisitOrdering>;
    visitsIncluded: Types.Scalars['Boolean']['input'];
    visitRequestFilters: Types.InputMaybe<Types.VisitRequestFilter>;
    visitRequestPagination: Types.InputMaybe<Types.PaginationInput>;
    visitRequestOrdering: Types.InputMaybe<Types.VisitRequestOrdering>;
    visitRequestsIncluded: Types.Scalars['Boolean']['input'];
};

/**
 * Extracts the common data-fetching logic shared by all four bulk assignment
 * tables: clinician, services, tentative date, and ready-for-promotion.
 *
 * Each table's Connected* component reduces to:
 *   1. Call this hook with its GQL query
 *   2. Check showSkeleton
 *   3. Wrap rows in its domain-specific context provider
 */
export const useConnectedBulkTable = <
    TData extends BulkQueryData<TVisitRequest, TVisit>,
    TVisitRequest extends VisitRequestTableRowFragment = VisitRequestTableRowFragment,
    TVisit extends VisitTableRowFragment = VisitTableRowFragment,
>(options: {
    query: TypedDocumentNode<TData, QueryVariables>;
}): UseConnectedBulkTableResult<TVisitRequest, TVisit> => {
    const { query } = options;
    const isBulkEditVisits = useFeatureFlagEnabled(
        FeatureFlag.DASHBOARD_INCLUDE_VISITS_IN_BULK_QUEUES,
    );

    // 1. Consume filters and sorting from context and set up unified pagination
    const {
        visitRequestFilters,
        visitFilters,
        visitRequestOrdering,
        visitOrdering,
        serverSideSort,
    } = useBulkTableContext();
    const [sortState] = serverSideSort;
    const { inputs, pagination, setListSize } = useUnifiedPaginationByPage([
        'visitRequests',
        'visits',
    ]);

    // 2. Fetch both lists of data
    const { data, previousData, loading } = useQuery<TData, QueryVariables>(query, {
        variables: {
            visitFilters: visitFilters === 'exclude' ? null : visitFilters,
            visitPagination: inputs.visits,
            visitOrdering,
            visitsIncluded: visitFilters !== 'exclude' && isBulkEditVisits,
            visitRequestFilters: visitRequestFilters === 'exclude' ? null : visitRequestFilters,
            visitRequestPagination: inputs.visitRequests,
            visitRequestOrdering,
            visitRequestsIncluded: visitRequestFilters !== 'exclude',
        },
        fetchPolicy: 'cache-and-network',
    });
    const currentData = data ?? previousData;

    // 3. Unify the two lists of data
    const { rows, consumed } = useUnifyItemList({
        visits: currentData?.visits?.items ?? [],
        visitRequests: currentData?.visitRequests?.items ?? [],
        sortState,
        pageSize: pagination.pageSize,
    });

    // 4. Update the unified pagination state with the new list sizes
    useEffect(() => {
        if (!data) {
            return;
        }
        setListSize.visitRequests({
            count: consumed.visitRequests,
            total: data.visitRequests?.totalCount ?? 0,
        });
        setListSize.visits({
            count: consumed.visits,
            total: data.visits?.totalCount ?? 0,
        });
    }, [data, setListSize, consumed.visitRequests, consumed.visits]);

    return {
        rows,
        pagination,
        serverSideSort,
        loading,
        data: currentData,
        showSkeleton: loading && !currentData,
    };
};
